
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { SolicitacaoFormData } from '@/types/solicitacao';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useUsers } from '@/hooks/useUsers';
import { useClients } from '@/hooks/useClients';
import { useCategorias } from '@/hooks/useCategorias';
import { useSLAs } from '@/hooks/useSLAs';
import { useGroups } from '@/hooks/useGroups';
import { useGroupUsers } from '@/hooks/useGroupUsers';
import { Categoria } from '@/types/categoria';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCategoriaName } from '@/utils/formatters';


interface SolicitacaoFormFieldsProps {
  form: UseFormReturn<SolicitacaoFormData>;
  excludeFields?: string[];
  readOnlyFields?: string[];
  filteredCategorias?: Categoria[];
  slaAplicaA?: 'incidente' | 'solicitacao' | 'problema';
  userSelectMode?: 'default' | 'searchable';
  groupSlaReadOnly?: boolean;
}

const SolicitacaoFormFields = ({ form, excludeFields = [], readOnlyFields = [], filteredCategorias, slaAplicaA, userSelectMode = 'default', groupSlaReadOnly = false }: SolicitacaoFormFieldsProps) => {
  const { users } = useUsers();
  const { clients } = useClients();
  const { categorias } = useCategorias();
  const { slas } = useSLAs();
  const { groups } = useGroups();

  // Buscar usuários do grupo selecionado para filtrar atendentes
  const selectedGroupId = form.watch('grupo_responsavel_id');
  const { groupUsers } = useGroupUsers(selectedGroupId);

  const [solicitanteOpen, setSolicitanteOpen] = React.useState(false);
  const [atendenteOpen, setAtendenteOpen] = React.useState(false);

  const sortedUsers = React.useMemo(() => {
    return [...users].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt', { sensitivity: 'base' }));
  }, [users]);

  // Usuários filtrados para o campo atendente (apenas do grupo selecionado)
  const availableAttendants = React.useMemo(() => {
    if (!selectedGroupId || groupUsers.length === 0) {
      return sortedUsers; // Se não há grupo selecionado, mostrar todos os usuários
    }
    return [...groupUsers].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt', { sensitivity: 'base' }));
  }, [selectedGroupId, groupUsers, sortedUsers]);

  // Limpar atendente se ele não fizer mais parte do grupo selecionado
  React.useEffect(() => {
    const currentAttendantId = form.getValues('atendente_id');
    if (currentAttendantId && selectedGroupId && groupUsers.length > 0) {
      const isAttendantInGroup = groupUsers.some(user => user.id === currentAttendantId);
      if (!isAttendantInGroup) {
        form.setValue('atendente_id', '');
      }
    }
  }, [selectedGroupId, groupUsers, form]);

  const currentStatus = form.watch('status');

  const slasToShow = React.useMemo(() => {
    return slaAplicaA ? slas.filter((s) => s.aplica_a === slaAplicaA) : slas;
  }, [slas, slaAplicaA]);

  // Use filtered categories if provided, otherwise use all categories
  const categoriasToShow = React.useMemo(() => {
    if (filteredCategorias) {
      return filteredCategorias;
    }
    // Filter categories by type if slaAplicaA is provided
    if (slaAplicaA) {
      return categorias.filter(cat => cat.tipo === slaAplicaA);
    }
    return categorias;
  }, [filteredCategorias, categorias, slaAplicaA]);
  const shouldRenderField = (fieldName: string) => {
    return !excludeFields.includes(fieldName);
  };

  const isFieldReadOnly = (fieldName: string) => {
    return readOnlyFields.includes(fieldName);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {shouldRenderField('titulo') && (
        <FormField
          control={form.control}
          name="titulo"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel>Título *</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Digite o título da solicitação" 
                  {...field} 
                  readOnly={isFieldReadOnly('titulo')}
                  className={isFieldReadOnly('titulo') ? "bg-muted" : ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('descricao') && (
        <FormField
          control={form.control}
          name="descricao"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormControl>
                <RichTextEditor
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Digite a descrição da solicitação..."
                  disabled={isFieldReadOnly('descricao')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('solicitante_id') && (
        <FormField
          control={form.control}
          name="solicitante_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Solicitante</FormLabel>
              {userSelectMode === 'searchable' ? (
                <Popover open={solicitanteOpen} onOpenChange={setSolicitanteOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={solicitanteOpen}
                        className="w-full justify-between"
                        disabled={isFieldReadOnly('solicitante_id')}
                      >
                        {sortedUsers.find(u => u.id === field.value)?.name || "Selecione o solicitante..."}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar usuário..." />
                      <CommandList>
                        <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
                        <CommandGroup>
                          {sortedUsers.map((user) => (
                            <CommandItem
                              key={user.id}
                              value={user.name}
                              onSelect={() => {
                                field.onChange(user.id);
                                setSolicitanteOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  field.value === user.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {user.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
               <Select onValueChange={field.onChange} value={field.value} disabled={isFieldReadOnly('solicitante_id')}>
                <FormControl>
                  <SelectTrigger className={isFieldReadOnly('solicitante_id') ? "bg-muted" : ""}>
                    <SelectValue placeholder="Selecione o solicitante" />
                  </SelectTrigger>
                </FormControl>
                  <SelectContent>
                    {sortedUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('client_id') && (
        <FormField
          control={form.control}
          name="client_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cliente</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={true}>
                <FormControl>
                  <SelectTrigger className="bg-muted">
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('categoria_id') && (
        <FormField
          control={form.control}
          name="categoria_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categoria</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isFieldReadOnly('categoria_id')}>
                <FormControl>
                  <SelectTrigger className={isFieldReadOnly('categoria_id') ? "bg-muted" : ""}>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categoriasToShow.map((categoria) => (
                    <SelectItem key={categoria.id} value={categoria.id}>
                      {formatCategoriaName(categoria)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('sla_id') && (
        <FormField
          control={form.control}
          name="sla_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>SLA</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={groupSlaReadOnly || isFieldReadOnly('sla_id')}>
                <FormControl>
                    <SelectTrigger className={groupSlaReadOnly || isFieldReadOnly('sla_id') ? "bg-muted" : ""}>
                      <SelectValue placeholder={groupSlaReadOnly || isFieldReadOnly('sla_id') ? "SLA será preenchido automaticamente" : "Selecione o SLA"} />
                    </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {slasToShow.map((sla) => (
                    <SelectItem key={sla.id} value={sla.id}>
                      {sla.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('urgencia') && (
        <FormField
          control={form.control}
          name="urgencia"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Urgência</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isFieldReadOnly('urgencia')}>
                <FormControl>
                  <SelectTrigger className={isFieldReadOnly('urgencia') ? "bg-muted" : ""}>
                    <SelectValue placeholder="Selecione a urgência" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="critica">Crítica</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('impacto') && (
        <FormField
          control={form.control}
          name="impacto"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Impacto</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isFieldReadOnly('impacto')}>
                <FormControl>
                  <SelectTrigger className={isFieldReadOnly('impacto') ? "bg-muted" : ""}>
                    <SelectValue placeholder="Selecione o impacto" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="baixo">Baixo</SelectItem>
                  <SelectItem value="medio">Médio</SelectItem>
                  <SelectItem value="alto">Alto</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('prioridade') && (
        <FormField
          control={form.control}
          name="prioridade"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Prioridade</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isFieldReadOnly('prioridade')}>
                <FormControl>
                  <SelectTrigger className={isFieldReadOnly('prioridade') ? "bg-muted" : ""}>
                    <SelectValue placeholder="Selecione a prioridade" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="critica">Crítica</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('canal_origem') && (
        <FormField
          control={form.control}
          name="canal_origem"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Canal de Origem</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isFieldReadOnly('canal_origem')}>
                <FormControl>
                  <SelectTrigger className={isFieldReadOnly('canal_origem') ? "bg-muted" : ""}>
                    <SelectValue placeholder="Selecione o canal" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="portal">Portal</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="telefone">Telefone</SelectItem>
                  <SelectItem value="chat">Chat</SelectItem>
                  <SelectItem value="presencial">Presencial</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('grupo_responsavel_id') && (
        <FormField
          control={form.control}
          name="grupo_responsavel_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Grupo Responsável</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={groupSlaReadOnly || isFieldReadOnly('grupo_responsavel_id')}>
                <FormControl>
                  <SelectTrigger className={groupSlaReadOnly || isFieldReadOnly('grupo_responsavel_id') ? "bg-muted" : ""}>
                    <SelectValue placeholder={groupSlaReadOnly || isFieldReadOnly('grupo_responsavel_id') ? "Grupo será preenchido automaticamente" : "Selecione o grupo responsável"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('atendente_id') && (
        <FormField
          control={form.control}
          name="atendente_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Atendente</FormLabel>
              {userSelectMode === 'searchable' ? (
                <Popover open={atendenteOpen} onOpenChange={setAtendenteOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={atendenteOpen}
                        className="w-full justify-between"
                        disabled={isFieldReadOnly('atendente_id')}
                       >
                         {availableAttendants.find(u => u.id === field.value)?.name || "Selecione o atendente..."}
                         <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar usuário..." />
                      <CommandList>
                        <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
                        <CommandGroup>
                          {availableAttendants.map((user) => (
                            <CommandItem
                              key={user.id}
                              value={user.name}
                              onSelect={() => {
                                field.onChange(user.id);
                                setAtendenteOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  field.value === user.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {user.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                <Select onValueChange={field.onChange} value={field.value} disabled={isFieldReadOnly('atendente_id')}>
                  <FormControl>
                    <SelectTrigger className={isFieldReadOnly('atendente_id') ? "bg-muted" : ""}>
                      <SelectValue placeholder="Selecione o atendente" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {availableAttendants.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('data_limite_resposta') && (
        <FormField
          control={form.control}
          name="data_limite_resposta"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Data Limite Resposta</FormLabel>
              <FormControl>
                <Input 
                  type="datetime-local" 
                  {...field} 
                  readOnly={isFieldReadOnly('data_limite_resposta')}
                  className={isFieldReadOnly('data_limite_resposta') ? "bg-muted" : ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('data_limite_resolucao') && (
        <FormField
          control={form.control}
          name="data_limite_resolucao"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Data Limite Resolução</FormLabel>
              <FormControl>
                <Input 
                  type="datetime-local" 
                  {...field} 
                  readOnly={isFieldReadOnly('data_limite_resolucao')}
                  className={isFieldReadOnly('data_limite_resolucao') ? "bg-muted" : ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('notas_internas') && (
        <FormField
          control={form.control}
          name="notas_internas"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel>Notas Internas</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Digite as notas internas..."
                  className="min-h-[80px]"
                  {...field}
                  readOnly={isFieldReadOnly('notas_internas')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {shouldRenderField('solucao_aplicada') && ['resolvida', 'fechada', 'reaberta'].includes(currentStatus) && (
        <>
          <FormField
            control={form.control}
            name="solucao_aplicada"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Solução Aplicada *</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Descreva a solução aplicada para resolver este ticket..."
                    className="min-h-[120px]"
                    {...field}
                    readOnly={isFieldReadOnly('solucao_aplicada')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {shouldRenderField('gerar_kb') && (
            <FormField
              control={form.control}
              name="gerar_kb"
              render={({ field }) => (
                <FormItem className="md:col-span-2 flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 bg-muted/50">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isFieldReadOnly('gerar_kb')}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="cursor-pointer">
                      Criar Conhecimento dessa Solicitação?
                    </FormLabel>
                    <FormDescription>
                      Ao marcar esta opção, um artigo da Base de Conhecimento será gerado automaticamente 
                      com base nesta solicitação e ficará disponível para revisão na seção de Conhecimento.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SolicitacaoFormFields;
